import {
  DeliveryLog,
  EligibleBatch,
  IncompleteDeliveryGroup,
  BulkCreateData,
  DeliveryCompletionData,
} from '../types/deliveryLog';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

const deliveryLogApiService = {
  /**
   * Fetch all delivery logs
   */
  fetchDeliveryLogs: async (): Promise<DeliveryLog[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/finished-product-deliveries`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch delivery logs');
      }

      return data.deliveryLogs;
    } catch (error) {
      console.error('Error fetching delivery logs from API:', error);
      throw error;
    }
  },

  /**
   * Create a new delivery log
   */
  createDeliveryLog: async (deliveryLog: DeliveryLog): Promise<DeliveryLog> => {
    try {
      const response = await fetch(`${API_BASE_URL}/finished-product-deliveries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(deliveryLog),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create delivery log');
      }

      return data.deliveryLog;
    } catch (error) {
      console.error('Error creating delivery log via API:', error);
      throw error;
    }
  },

  /**
   * Update an existing delivery log
   */
  updateDeliveryLog: async (deliveryLog: DeliveryLog): Promise<DeliveryLog> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/finished-product-deliveries/${deliveryLog.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(deliveryLog),
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update delivery log');
      }

      return data.deliveryLog;
    } catch (error) {
      console.error('Error updating delivery log via API:', error);
      throw error;
    }
  },

  /**
   * Delete a delivery log
   */
  deleteDeliveryLog: async (id: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/finished-product-deliveries/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete delivery log');
      }
    } catch (error) {
      console.error('Error deleting delivery log via API:', error);
      throw error;
    }
  },

  /**
   * Fetch batches eligible for delivery
   */
  fetchEligibleBatches: async (fillingId?: string): Promise<EligibleBatch[]> => {
    try {
      const url = fillingId
        ? `${API_BASE_URL}/finished-product-deliveries/eligible-batches?fillingId=${fillingId}`
        : `${API_BASE_URL}/finished-product-deliveries/eligible-batches`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch eligible batches');
      }

      return data.eligibleBatches;
    } catch (error) {
      console.error('Error fetching eligible batches from API:', error);
      throw error;
    }
  },

  /**
   * Bulk create delivery logs with a shared delivery_group_id
   */
  bulkCreateDeliveryLogs: async (bulkData: BulkCreateData): Promise<DeliveryLog[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/finished-product-deliveries/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bulkData),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to bulk create delivery logs');
      }

      return data.deliveryLogs;
    } catch (error) {
      console.error('Error bulk creating delivery logs via API:', error);
      throw error;
    }
  },

  /**
   * Fetch incomplete delivery groups
   */
  fetchIncompleteDeliveryGroups: async (): Promise<IncompleteDeliveryGroup[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/finished-product-deliveries/incomplete`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch incomplete delivery groups');
      }

      return data.incompleteDeliveryGroups;
    } catch (error) {
      console.error('Error fetching incomplete delivery groups from API:', error);
      throw error;
    }
  },

  /**
   * Complete a delivery group with temperature, initials, etc.
   */
  completeDeliveryGroup: async (
    groupId: string,
    completionData: DeliveryCompletionData
  ): Promise<DeliveryLog[]> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/finished-product-deliveries/complete/${groupId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(completionData),
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to complete delivery group');
      }

      return data.deliveryLogs;
    } catch (error) {
      console.error('Error completing delivery group via API:', error);
      throw error;
    }
  },
};

export default deliveryLogApiService;
